<script lang="ts" setup>
  import dayjs from 'dayjs'
  import utc from 'dayjs/plugin/utc'
  import formatRequestState from '@/shared/utils/formatRequestState'
  import { useUrlPresigned } from '@/shared/composables/useUrlPresigned'
  dayjs.extend(utc)

  defineProps<{
    items: {
      letterName: string
      status: string
      urlFile: string
      resolutionDate: string | null
      createdAt: string
    }
  }>()

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'approved':
        return 'text-green-500'
      case 'rejected':
      case 'rejected':
        return 'text-red-500'
      case 'pending':
      case 'pending':
        return 'text-orange-500'
      default:
        return 'text-gray-500'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'approved':
        return 'pi pi-check-circle'
      case 'rejected':
      case 'rejected':
        return 'pi pi-times-circle'
      case 'pending':
      case 'pending':
        return 'pi pi-clock'
      default:
        return 'pi pi-file'
    }
  }

  const downloadFile = async (url: string, fileName: string) => {
    const presignedUrl = await useUrlPresigned(url)
    window.open(presignedUrl, '_blank')
  }
</script>

<template>
  <div class="surface-card border-round-xl shadow-2 p-4 mt-3">
    <div class="flex flex-column justify-content-between">
      <!-- Header with letter name and status -->
      <div class="flex align-items-center justify-content-between mb-3">
        <div class="flex align-items-center">
          <i class="pi pi-file-pdf text-primary mr-2 text-xl"></i>
          <div>
            <h6 class="font-semibold text-primary mb-1 text-sm">
              {{ items.letterName }}
            </h6>
            <p class="text-600 text-xs m-0">
              Creada:
              {{ dayjs(items.createdAt).utc().format('DD/MM/YYYY HH:mm') }}
            </p>
            <p v-if="items.resolutionDate" class="text-600 text-xs m-0">
              Resuelta:
              {{ dayjs(items.resolutionDate).utc().format('DD/MM/YYYY HH:mm') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Status badge -->
      <div class="flex justify-content-end">
        <i
          :class="[
            getStatusIcon(items.status),
            getStatusColor(items.status),
            'mr-2',
          ]"
        ></i>
        <span :class="getStatusColor(items.status)" class="text-sm font-medium">
          {{ formatRequestState(items.status) }}
        </span>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-content-end mt-2">
        <Button
          label=""
          icon="pi pi-link"
          class="p-button-sm p-button-secondary font-light"
          @click="downloadFile(items.urlFile, items.letterName)"
          :disabled="!items.urlFile"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .surface-card {
    transition: all 0.3s ease;
  }

  .surface-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }

  .text-xl {
    font-size: 1.25rem;
  }
</style>
